import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import Input from '../shared/Input';
import Drawer from '../shared/Drawer';
import Autocomplete from '../shared/Autocomplete';
import PhoneInput from '../shared/PhoneInput';
import ColorInput from '../shared/ColorInput';
import Button from '../shared/Button';
import Dropzone from '../shared/dropzone/Dropzone';
import ConfirmationModal from '../modals/ConfirmationModal';

import countries from '../../constants/countries';

const countriesList = countries.map((c) => c.name);
const getCompanyLogoUrl = (logo) => (
  typeof logo === 'string' ? logo : logo?.downloadUrl || ''
);

const CompanyModal = (props) => {
  const {
    mode = 'create',
    open,
    onClose,
    onSubmit,
    loading,
    initialValues,
  } = props;

  const [name, setName] = useState('');
  const [countries, setCountries] = useState([]);
  const [address, setAddress] = useState('');
  const [state, setState] = useState('');
  const [postCode, setPostCode] = useState('');
  const [country, setCountry] = useState(null);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [color, setColor] = useState('#217CEE');
  const [logo, setLogo] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [initialLogo, setInitialLogo] = useState(null);
  const [sidebarLogo, setSidebarLogo] = useState(null);
  const [sidebarLogoFile, setSidebarLogoFile] = useState(null);
  const [initialSidebarLogo, setInitialSidebarLogo] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmResolver, setConfirmResolver] = useState(null);
  const [countryToRemove, setCountryToRemove] = useState(null);

  useEffect(() => {
    if (open && mode === 'update') {
      setName(initialValues?.name || '');
      setCountries(initialValues?.countries || []);
      setAddress(initialValues?.address || '');
      setCountry(initialValues?.country || '');
      setState(initialValues?.state || '');
      setPostCode(initialValues?.postCode || '');
      setEmail(initialValues?.email || '');
      setPhone(initialValues?.phone || '');
      setColor(initialValues?.color || '#217CEE');
      setLogo(initialValues?.logo || null);
      setInitialLogo(initialValues?.logo || null);
      setLogoFile(null);
      setSidebarLogo(initialValues?.sidebarLogo || null);
      setInitialSidebarLogo(initialValues?.sidebarLogo || null);
      setSidebarLogoFile(null);
    }

    if (open && mode === 'create') {
      setLogo(null);
      setInitialLogo(null);
      setLogoFile(null);
      setSidebarLogo(null);
      setInitialSidebarLogo(null);
      setSidebarLogoFile(null);
    }
  }, [open, mode, initialValues]);

  const resetValues = () => {
    setName('');
    setCountries([]);
    setCountry(null);
    setAddress( '');
    setState('');
    setPostCode('');
    setEmail('');
    setPhone('');
    setColor('#217CEE');
    setLogo(null);
    setInitialLogo(null);
    setLogoFile(null);
    setSidebarLogo(null);
    setInitialSidebarLogo(null);
    setSidebarLogoFile(null);
  };

  const handleSubmit = async () => {
    const formObj = {
      name: name || '',
      countries,
      address,
      country,
      state,
      postCode,
      email,
      phone,
      color,
      logo,
      logoFile,
      previousLogo: initialLogo,
      logoRemoved: !logoFile && !logo && !!initialLogo,
      sidebarLogo,
      sidebarLogoFile,
      previousSidebarLogo: initialSidebarLogo,
      sidebarLogoRemoved: !sidebarLogoFile && !sidebarLogo && !!initialSidebarLogo,
    };
    await onSubmit(formObj);
    resetValues();
  };

  const isDisabled = () => {
    return !name || !countries?.length;
  };

  const handleConfirmRemoveCountry = (option) => {
    return new Promise((resolve) => {
      setCountryToRemove(option);
      setConfirmOpen(true);
      setConfirmResolver(() => resolve);
    });
  };

  const handleConfirmSubmit = () => {
    confirmResolver?.(true);
    setConfirmOpen(false);
    setCountryToRemove(null);
  };

  const handleConfirmClose = () => {
    confirmResolver?.(false);
    setConfirmOpen(false);
    setCountryToRemove(null);
  };

  const handleLogoUpload = (acceptedFiles) => {
    setLogoFile(acceptedFiles[0] || null);
  };

  const handleClearSelectedLogo = () => {
    setLogoFile(null);
  };

  const handleRemoveCurrentLogo = () => {
    setLogo(null);
    setLogoFile(null);
  };

  const handleSidebarLogoUpload = (acceptedFiles) => {
    setSidebarLogoFile(acceptedFiles[0] || null);
  };

  const handleClearSelectedSidebarLogo = () => {
    setSidebarLogoFile(null);
  };

  const handleRemoveCurrentSidebarLogo = () => {
    setSidebarLogo(null);
    setSidebarLogoFile(null);
  };

  const logoPreviewUrl = logoFile?.preview || getCompanyLogoUrl(logo);
  const logoFileName = logoFile?.name || (typeof logo === 'string' ? 'Current Logo' : logo?.fileName || '');
  const sidebarLogoPreviewUrl = sidebarLogoFile?.preview || getCompanyLogoUrl(sidebarLogo);
  const sidebarLogoFileName = sidebarLogoFile?.name || (typeof sidebarLogo === 'string' ? 'Current Sidebar Logo' : sidebarLogo?.fileName || '');

  return (
    <Drawer
      open={open}
      onClose={() => {
        resetValues();
        onClose();
      }}
      title={mode === 'update' ? 'Edit Company' : 'Create Company'}
      subtitle="Enter company details"
      submitText={mode === 'update' ? 'Save Changes' : 'Create'}
      disabled={isDisabled()}
      loading={loading}
      onSubmit={handleSubmit}
    >
      <Box sx={{ minWidth: { xs: 'auto', md: '560px' }, maxWidth: { xs: 'auto', md: '600px' } }}>
        <Grid container spacing={1}>
          <Grid size={{ xs: 12 }}>
            <Input
              id="name"
              type="text"
              placeholder=""
              value={name}
              onChange={(e) => setName(e.target.value)}
              label="Company Name"
              required
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              value={countries}
              onChange={setCountries}
              options={countriesList}
              label="Administration Countries"
              multiple
              required
              confirmOnRemove={mode === 'update'}
              onConfirmRemove={handleConfirmRemoveCountry}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Input
              id="address"
              type="text"
              placeholder=""
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              label="Address"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Input
              id="state"
              type="text"
              placeholder=""
              value={state}
              onChange={(e) => setState(e.target.value)}
              label="State"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Input
              id="postCode"
              type="text"
              placeholder=""
              value={postCode}
              onChange={(e) => setPostCode(e.target.value)}
              label="Post Code"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Input
              id="email"
              type="text"
              placeholder=""
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              label="Email"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box>
              <Box sx={{ display: 'flex', marginBottom: '2px' }}>
                <Typography sx={{ fontSize: '14px' }}>Phone Number</Typography>
              </Box>
              <PhoneInput
                value={phone}
                onChange={setPhone}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              value={country}
              onChange={setCountry}
              options={countriesList}
              label="Country HQ"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <ColorInput
              label="Company Color"
              value={color}
              onChange={setColor}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Box sx={{ mt: 1 }}>
              <Box sx={{ mb: 1, fontSize: 14, color: 'var(--color-text-primary)' }}>
                Company Logo
              </Box>
              <Dropzone
                multiple={false}
                files={logoFile ? [logoFile] : []}
                onUpload={handleLogoUpload}
                type="image"
                accept="image/jpeg, image/png, image/svg+xml"
                text="Drag and drop a company logo (.png, .jpg, .svg) here or click to upload"
                hideFilesList
                loading={loading}
              />
              {logoPreviewUrl && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                  <Box
                    component="img"
                    src={logoPreviewUrl}
                    alt={`${name || 'company'}-logo`}
                    sx={{
                      width: 56,
                      height: 56,
                      objectFit: 'contain',
                      borderRadius: '8px',
                      border: '1px solid var(--color-border)',
                      backgroundColor: 'var(--color-surface)',
                      p: 1,
                      mr: 1.5,
                    }}
                  />
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                      {logoFile ? 'Selected Logo' : 'Current Logo'}
                    </Typography>
                    {!!logoFileName && (
                      <Typography sx={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                        {logoFileName}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              {(!!logoFile || !!logo) && (
                <Box sx={{ mt: 1.5 }}>
                  <Button
                    type="secondary"
                    text={logoFile ? 'Clear Selected Logo' : 'Delete Current Logo'}
                    icon={<DeleteOutlineIcon fontSize="small" />}
                    onClick={logoFile ? handleClearSelectedLogo : handleRemoveCurrentLogo}
                  />
                </Box>
              )}
            </Box>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Box sx={{ mt: 1 }}>
              <Box sx={{ mb: 1, fontSize: 14, color: 'var(--color-text-primary)' }}>
                Sidebar Logo
              </Box>
              <Dropzone
                multiple={false}
                files={sidebarLogoFile ? [sidebarLogoFile] : []}
                onUpload={handleSidebarLogoUpload}
                type="image"
                accept="image/jpeg, image/png, image/svg+xml"
                text="Drag and drop a wide sidebar logo (.png, .jpg, .svg) here or click to upload"
                hideFilesList
                loading={loading}
              />
              {sidebarLogoPreviewUrl && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                  <Box
                    component="img"
                    src={sidebarLogoPreviewUrl}
                    alt={`${name || 'company'}-sidebar-logo`}
                    sx={{
                      width: 168,
                      height: 48,
                      objectFit: 'contain',
                      borderRadius: '8px',
                      border: '1px solid var(--color-border)',
                      backgroundColor: 'var(--color-surface)',
                      p: 1,
                      mr: 1.5,
                    }}
                  />
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                      {sidebarLogoFile ? 'Selected Sidebar Logo' : 'Current Sidebar Logo'}
                    </Typography>
                    {!!sidebarLogoFileName && (
                      <Typography sx={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                        {sidebarLogoFileName}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              {(!!sidebarLogoFile || !!sidebarLogo) && (
                <Box sx={{ mt: 1.5 }}>
                  <Button
                    type="secondary"
                    text={sidebarLogoFile ? 'Clear Selected Sidebar Logo' : 'Delete Current Sidebar Logo'}
                    icon={<DeleteOutlineIcon fontSize="small" />}
                    onClick={sidebarLogoFile ? handleClearSelectedSidebarLogo : handleRemoveCurrentSidebarLogo}
                  />
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {mode === 'update' && (
        <ConfirmationModal
          open={confirmOpen}
          onClose={handleConfirmClose}
          onSubmit={handleConfirmSubmit}
          title="Remove Country?"
          text={
            countryToRemove
              ? `Are you sure you want to remove "${countryToRemove}" from administration countries?`
              : ''
          }
          variant="warning"
          submitText="Remove"
          closeText="Cancel"
        />
      )}
    </Drawer>
  );
};

CompanyModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default CompanyModal;
